/**
 * AdminPlayerAdsTab — admin-only manager for the cover-rotation ad slot
 * on the audio player. Lets the admin add new ads (image URL + sponsor +
 * weight + click URL), toggle them on/off, and see live impressions/clicks.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Megaphone, Plus, Loader2, ExternalLink, Eye, MousePointerClick, Trash2, Power,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPlayerAdsTab = ({ role = 'admin' }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [draft, setDraft] = useState({
    label: '', image_url: '', click_url: '', sponsor_name: '', weight: 1,
  });
  const [creating, setCreating] = useState(false);
  const readOnly = role !== 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/player-ads`);
      if (r.ok) {
        const d = await r.json();
        setAds(d.ads || []);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitNew = async () => {
    if (!draft.label.trim() || !draft.image_url.trim()) {
      toast.error('Label and image URL are required.');
      return;
    }
    setCreating(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/player-ads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: draft.label.trim(),
          image_url: draft.image_url.trim(),
          click_url: draft.click_url.trim() || undefined,
          sponsor_name: draft.sponsor_name.trim() || undefined,
          weight: Number(draft.weight) || 1,
          is_active: true,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Create failed');
      }
      toast.success('Ad created.');
      setDraft({ label: '', image_url: '', click_url: '', sponsor_name: '', weight: 1 });
      load();
    } catch (e) {
      toast.error(e.message || 'Create failed');
    } finally { setCreating(false); }
  };

  const togglePower = async (ad) => {
    setBusy((b) => ({ ...b, [ad.ad_id]: 'toggle' }));
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/player-ads/${ad.ad_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !ad.is_active }),
      });
      if (!r.ok) throw new Error('Toggle failed');
      load();
    } catch (e) {
      toast.error(e.message || 'Toggle failed');
    } finally { setBusy((b) => ({ ...b, [ad.ad_id]: null })); }
  };

  const remove = async (ad) => {
    if (!window.confirm(`Delete "${ad.label}"?`)) return;
    setBusy((b) => ({ ...b, [ad.ad_id]: 'delete' }));
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/player-ads/${ad.ad_id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Ad deleted.');
      load();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    } finally { setBusy((b) => ({ ...b, [ad.ad_id]: null })); }
  };

  const totalImpressions = ads.reduce((s, a) => s + (a.impressions || 0), 0);
  const totalClicks = ads.reduce((s, a) => s + (a.clicks || 0), 0);
  const ctr = totalImpressions ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';

  return (
    <div className="space-y-5" data-testid="admin-player-ads-tab">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-white inline-flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-amber-300" />
            Player ads
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Each ad alternates with the cover artwork on the audio player. Higher weight = more frequent.
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-400">
          <span><span className="text-slate-500 uppercase tracking-widest mr-1">Impressions</span> <span className="text-white font-bold tabular-nums">{totalImpressions.toLocaleString()}</span></span>
          <span><span className="text-slate-500 uppercase tracking-widest mr-1">Clicks</span> <span className="text-white font-bold tabular-nums">{totalClicks.toLocaleString()}</span></span>
          <span><span className="text-slate-500 uppercase tracking-widest mr-1">CTR</span> <span className="text-emerald-300 font-bold tabular-nums">{ctr}%</span></span>
        </div>
      </header>

      {/* Composer (admin-only) */}
      {!readOnly && (
        <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4" data-testid="player-ad-composer">
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">New ad</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="Internal label (e.g. June Smarter+ promo)"
              className="rounded-md bg-slate-950 border border-slate-700 text-white text-sm px-2.5 py-1.5"
              data-testid="player-ad-label"
            />
            <input
              value={draft.sponsor_name}
              onChange={(e) => setDraft({ ...draft, sponsor_name: e.target.value })}
              placeholder="Sponsor name (shown to readers)"
              className="rounded-md bg-slate-950 border border-slate-700 text-white text-sm px-2.5 py-1.5"
              data-testid="player-ad-sponsor"
            />
            <input
              value={draft.image_url}
              onChange={(e) => setDraft({ ...draft, image_url: e.target.value })}
              placeholder="Image URL (square recommended)"
              className="sm:col-span-2 rounded-md bg-slate-950 border border-slate-700 text-white text-sm px-2.5 py-1.5"
              data-testid="player-ad-image-url"
            />
            <input
              value={draft.click_url}
              onChange={(e) => setDraft({ ...draft, click_url: e.target.value })}
              placeholder="Click URL (optional)"
              className="rounded-md bg-slate-950 border border-slate-700 text-white text-sm px-2.5 py-1.5"
              data-testid="player-ad-click-url"
            />
            <div className="flex items-center gap-2">
              <label className="text-[11px] uppercase tracking-widest font-bold text-slate-400 shrink-0">Weight</label>
              <input
                type="number" min="1" max="10"
                value={draft.weight}
                onChange={(e) => setDraft({ ...draft, weight: e.target.value })}
                className="w-20 rounded-md bg-slate-950 border border-slate-700 text-white text-sm px-2.5 py-1.5"
                data-testid="player-ad-weight"
              />
              <button
                type="button"
                onClick={submitNew}
                disabled={creating}
                className="ml-auto px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 text-slate-900 text-xs font-bold inline-flex items-center gap-1.5"
                data-testid="player-ad-create"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add ad
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </p>
      ) : ads.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-8 text-center">
          No ads yet. Add one above and it will start rotating with covers immediately.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ads.map((ad) => {
            const adCtr = ad.impressions ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : '0.00';
            return (
              <article
                key={ad.ad_id}
                className={`rounded-xl border p-3 flex items-center gap-3 ${ad.is_active ? 'border-amber-500/40 bg-amber-500/5' : 'border-slate-800 bg-slate-900/60 opacity-60'}`}
                data-testid={`player-ad-row-${ad.ad_id}`}
              >
                <img src={ad.image_url} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-700 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-white truncate">{ad.label}</p>
                    {ad.sponsor_name && <span className="text-[10px] text-amber-300">· {ad.sponsor_name}</span>}
                    <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">w {ad.weight}</span>
                    {!ad.is_active && <span className="text-[10px] uppercase font-bold text-rose-300">OFF</span>}
                  </div>
                  {ad.click_url && (
                    <a href={ad.click_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-indigo-300 hover:text-indigo-200 truncate inline-flex items-center gap-1">
                      {ad.click_url} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <div className="flex items-center gap-3 text-[11px] mt-1 text-slate-300">
                    <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {(ad.impressions || 0).toLocaleString()}</span>
                    <span className="inline-flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> {(ad.clicks || 0).toLocaleString()}</span>
                    <span className="text-emerald-300 font-bold tabular-nums">{adCtr}% CTR</span>
                  </div>
                </div>
                {!readOnly && (
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => togglePower(ad)}
                      disabled={!!busy[ad.ad_id]}
                      className={`p-2 rounded-md ${ad.is_active ? 'bg-emerald-600/80 hover:bg-emerald-500 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-200'}`}
                      title={ad.is_active ? 'Turn off' : 'Turn on'}
                      data-testid={`player-ad-toggle-${ad.ad_id}`}
                    >
                      {busy[ad.ad_id] === 'toggle' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Power className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(ad)}
                      disabled={!!busy[ad.ad_id]}
                      className="p-2 rounded-md bg-rose-600/20 hover:bg-rose-600 hover:text-white text-rose-300"
                      title="Delete"
                      data-testid={`player-ad-delete-${ad.ad_id}`}
                    >
                      {busy[ad.ad_id] === 'delete' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminPlayerAdsTab;
